import {
  CLIENT_QUESTION_DEFAULT_PROMPTS,
  CLIENT_QUESTION_KIND_LABELS,
  CLIENT_QUESTION_KINDS,
  type ClientQuestion,
  type ClientQuestionKind,
} from '@hvac/contracts';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  PageHeader,
  Skeleton,
} from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { sessionQuery } from '../../shared/api/queries.js';
import {
  askClientQuestion,
  questionKeys,
  questionsQuery,
  withdrawClientQuestion,
} from '../../shared/api/question-queries.js';

/**
 * Questions to the client: what has been asked, what came back, and asking
 * another. Each question is a link the client answers without signing in.
 * Until a mail account exists the link is copied and sent by hand, and the
 * page says so.
 */
export function QuestionsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const membership = session.organizations.find(
    (entry) => entry.organization.id === organizationId,
  );
  const canAsk = membership?.permissions.includes('transaction:update') ?? false;
  const queryClient = useQueryClient();
  const questions = useQuery(questionsQuery(organizationId));
  const [kind, setKind] = useState<ClientQuestionKind>('other');
  const [prompt, setPrompt] = useState('');
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: questionKeys.all(organizationId) });

  const ask = useMutation({
    mutationFn: () => askClientQuestion(organizationId, { kind, prompt: prompt.trim() }),
    onSuccess: (asked) => {
      setLink(asked.link);
      setCopied(false);
      setPrompt('');
      setError(null);
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The question was not created.'),
  });

  const withdraw = useMutation({
    mutationFn: (questionId: string) => withdrawClientQuestion(organizationId, questionId),
    onSuccess: refresh,
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The question was not withdrawn.'),
  });

  const rows = questions.data ?? [];
  const open = rows.filter((row) => row.status === 'open');
  const answered = rows.filter((row) => row.status === 'answered');

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Work"
        title="Questions to the client"
        description={
          questions.data === undefined
            ? 'Reading…'
            : rows.length === 0
              ? 'Nothing asked yet. A question is a link the client opens on their phone: they read what it is about, type an answer or photograph the receipt, and it files itself.'
              : `${String(open.length)} waiting for an answer, ${String(answered.length)} answered.`
        }
      />

      {error === null ? null : <Alert tone="danger">{error}</Alert>}

      {link === null ? null : (
        <Alert tone="success" title="The question is ready to send">
          <p>
            Send this link to the client by text or email. It works for thirty days and needs no
            sign-in.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="max-w-full break-all rounded bg-surface-sunken px-2 py-1 text-xs">
              {link}
            </code>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                navigator.clipboard
                  .writeText(link)
                  .then(() => setCopied(true))
                  .catch(() => setCopied(false));
              }}
            >
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </div>
        </Alert>
      )}

      {!canAsk ? null : (
        <Card>
          <CardHeader
            title="Ask something"
            description="For a missing receipt, ask from the Bank page beside the charge; the question then carries the date and amount. Anything else goes here."
          />
          <CardBody className="flex flex-col gap-3">
            <div className="grid gap-3 sm:grid-cols-[14rem_1fr]">
              <Field label="Kind">
                {({ inputProps }) => (
                  <select
                    {...inputProps}
                    value={kind}
                    onChange={(event) => {
                      const next = event.target.value as ClientQuestionKind;
                      setKind(next);
                      if (
                        prompt.trim() === '' ||
                        Object.values(CLIENT_QUESTION_DEFAULT_PROMPTS).includes(prompt)
                      ) {
                        setPrompt(CLIENT_QUESTION_DEFAULT_PROMPTS[next]);
                      }
                    }}
                    className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
                  >
                    {CLIENT_QUESTION_KINDS.map((option) => (
                      <option key={option} value={option}>
                        {CLIENT_QUESTION_KIND_LABELS[option]}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <Field label="The question, as the client will read it">
                {({ inputProps }) => (
                  <Input
                    {...inputProps}
                    value={prompt}
                    onChange={(event) => setPrompt(event.target.value)}
                    placeholder="Was the $1,240 e-transfer on October 3 a customer payment, and from whom?"
                  />
                )}
              </Field>
            </div>
            <div>
              <Button
                onClick={() => ask.mutate()}
                isLoading={ask.isPending}
                disabled={prompt.trim().length < 3}
              >
                Create the link
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Asked" description="Open questions first." />
        <CardBody className="flex flex-col gap-3">
          {questions.isPending ? (
            <Skeleton className="h-16 w-full" />
          ) : rows.length === 0 ? (
            <p className="text-sm text-content-muted">Nothing yet.</p>
          ) : (
            rows.map((row) => (
              <QuestionRow
                key={row.id}
                question={row}
                canAsk={canAsk}
                onWithdraw={() => withdraw.mutate(row.id)}
              />
            ))
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function QuestionRow({
  question,
  canAsk,
  onWithdraw,
}: {
  question: ClientQuestion;
  canAsk: boolean;
  onWithdraw: () => void;
}) {
  const tone =
    question.status === 'open'
      ? 'text-warning'
      : question.status === 'answered'
        ? 'text-success'
        : 'text-content-muted';
  return (
    <div className="flex flex-col gap-1 border-t border-border pt-3 text-sm first:border-0 first:pt-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium">
          {CLIENT_QUESTION_KIND_LABELS[question.kind]}
          <span className={['ml-2 text-xs', tone].join(' ')}>
            {question.status === 'open'
              ? `waiting since ${question.askedAt.slice(0, 10)}`
              : question.status === 'answered'
                ? `answered ${question.answeredAt?.slice(0, 10) ?? ''}`
                : question.status}
          </span>
        </span>
        {canAsk && question.status === 'open' ? (
          <button
            type="button"
            className="text-xs text-content-muted hover:text-danger"
            onClick={onWithdraw}
          >
            Withdraw
          </button>
        ) : null}
      </div>
      <p className="text-content-muted">{question.prompt}</p>
      {question.about === null ? null : (
        <p className="text-xs text-content-subtle">About: {question.about}</p>
      )}
      {question.status !== 'answered' ? null : (
        <p>
          <span className="text-content-muted">{question.answeredName ?? 'The client'}: </span>
          {question.answerText ?? 'sent a photo'}
          {question.answerDocumentId === null ? null : (
            <>
              {' · '}
              <Link
                to="/documents/$documentId"
                params={{ documentId: question.answerDocumentId }}
                className="text-brand-700 hover:underline"
              >
                the document
              </Link>
            </>
          )}
        </p>
      )}
    </div>
  );
}
